import { Delegate } from './delegate.js';

const MOBILE_LAYOUT_QUERY = '(max-width: 767.98px)';

if (!customElements.get('combine-it-with-slider')) {
  customElements.define(
    'combine-it-with-slider',
    class CombineItWithSlider extends HTMLElement {
      constructor() {
        super();
        this.delegate = null;
        this.initRaf = null;
        this.sliderInstance = null;
        this.mobileLayoutMq = null;
        this.onMobileLayoutChange = null;
        this.selectors = {
          track: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };
      }

      connectedCallback() {
        this.delegate = new Delegate(this);
        this.blockId = this.dataset.blockId;
        this.block = this.closest(`[data-id="${this.blockId}"]`);
        this.track = this.querySelector(this.selectors.track);
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.mobileOnlySlider = this.dataset.mobileOnlySlider === 'true';
        this.sliderOptions = this.parseSliderOptions();

        if (!this.enableSlider || !this.track) return;

        if (this.mobileOnlySlider) {
          this.#bindMobileOnlyLayout();
          this.#syncMobileOnlyLayout();
          return;
        }

        this.init();
      }

      disconnectedCallback() {
        this.#unbindMobileOnlyLayout();
        this.delegate?.destroy();
        this.delegate = null;
        if (this.initRaf) {
          cancelAnimationFrame(this.initRaf);
          this.initRaf = null;
        }
        this.destroy();
      }

      /** Initializes Swiper markup and carousel instance. */
      init() {
        if (typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) {
          if (this.initRaf) return;
          this.initRaf = requestAnimationFrame(() => {
            this.initRaf = null;
            if (this.isConnected) this.init();
          });
          return;
        }

        if (this.sliderInstance) {
          this.destroy();
        }

        this.#enableSwiperMarkup();
        this.sliderOptions = this.resolveSliderOptions(this.sliderOptions);

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, [FoxTheme.Swiper.Mousewheel]);
        this.sliderInstance.init();
      }

      /** Parses the Liquid-authored slider config from the HTML data attribute. */
      parseSliderOptions() {
        try {
          return JSON.parse(this.dataset.sliderOptions || '{}');
        } catch (error) {
          return {};
        }
      }

      /** Converts selector strings from Liquid config into element references required by Swiper. */
      resolveSliderOptions(options) {
        const resolved = { ...options };

        if (resolved.navigation) {
          resolved.navigation = {
            ...resolved.navigation,
            nextEl: this.block?.querySelector(resolved.navigation.nextEl || this.selectors.nextEl),
            prevEl: this.block?.querySelector(resolved.navigation.prevEl || this.selectors.prevEl),
          };
        }

        return resolved;
      }

      destroy() {
        if (!this.mobileOnlySlider) {
          this.#enableGridMarkup();
        }
        if (!this.sliderInstance?.slider) return;
        try {
          this.sliderInstance.slider.destroy(true, true);
        } catch (_) {
          /* noop */
        }
        this.sliderInstance = null;
      }

      /** Subscribes to mobile layout changes for the 2-product mobile-only carousel mode. */
      #bindMobileOnlyLayout() {
        if (!window.matchMedia) return;
        this.mobileLayoutMq = window.matchMedia(MOBILE_LAYOUT_QUERY);
        this.onMobileLayoutChange = () => this.#syncMobileOnlyLayout();
        this.mobileLayoutMq.addEventListener('change', this.onMobileLayoutChange);
      }

      /** Unsubscribes from mobile layout listener. */
      #unbindMobileOnlyLayout() {
        if (this.mobileLayoutMq && this.onMobileLayoutChange) {
          this.mobileLayoutMq.removeEventListener('change', this.onMobileLayoutChange);
        }
        this.mobileLayoutMq = null;
        this.onMobileLayoutChange = null;
      }

      /** Enables swiper on mobile and static grid on desktop for 2-product blocks. */
      #syncMobileOnlyLayout() {
        const isMobile = this.mobileLayoutMq?.matches ?? window.matchMedia(MOBILE_LAYOUT_QUERY).matches;

        if (isMobile) {
          if (!this.sliderInstance) this.init();
          return;
        }

        if (this.sliderInstance) {
          this.destroy();
        }
        this.#enableGridMarkup();
      }

      /** Applies Swiper classes required before carousel initialization. */
      #enableSwiperMarkup() {
        this.classList.add(this.classes.swiper, 'swiper-equal-height');
        this.classList.remove(this.classes.grid);
        this.track.classList.remove(this.classes.grid);
        this.track.classList.add(this.classes.swiperWrapper);
        this.track.querySelectorAll('.f-column').forEach((slide) => {
          slide.classList.remove('f-column');
          slide.classList.add('swiper-slide');
        });
      }

      /** Restores static grid markup after destroying the mobile-only carousel. */
      #enableGridMarkup() {
        this.classList.remove(this.classes.swiper, 'swiper-equal-height');
        this.classList.add(this.classes.grid);
        this.track.classList.remove(this.classes.swiperWrapper);
        this.track.classList.add(this.classes.grid);
        this.track.querySelectorAll('.swiper-slide').forEach((slide) => {
          slide.classList.remove('swiper-slide');
          slide.style.width = '';
          slide.classList.add('f-column');
        });
      }
    }
  );
}
